import { siteConfig } from '../data/siteConfig';
import { features } from '../data/features';
import { screenshots } from '../data/screenshots';
import FeatureCard from '../components/FeatureCard';
import ScreenshotGallery from '../components/ScreenshotGallery';
import Logo from '../components/Logo';
import DownloadButton from '../components/DownloadButton';
import TrailerVideo from '../components/TrailerVideo';

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-10 md:gap-16">
            {/* Logo */}
            <Logo className="hidden sm:block w-28 h-28 md:w-40 md:h-40 shrink-0" />

            {/* Text */}
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-primary-500 dark:text-primary-400 mb-3">
                {siteConfig.name} {siteConfig.version}
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">
                Take full control of your Minecraft Bedrock worlds.
              </h1>
              <p className="mt-5 text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
                Change game rules, edit inventories and villager trades, prune chunks on a real map of your world, work out why a village stopped breeding, and tweak just about everything else &mdash; on your own computer, on your Realms, or on your server. Just download and run &mdash; no install needed, on Windows or Linux.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <DownloadButton platform="windows" primary />
                <DownloadButton platform="linux" />
              </div>
              <p className="mt-4 text-sm text-slate-500 dark:text-slate-500">
                {siteConfig.platform} &middot; Minecraft 1.21 &ndash; 26.0+ &middot; Free to use &middot; No account required
              </p>
            </div>

            {/* Avatar */}
            <img
              src="/images/avatars/control_avatar.png"
              alt=""
              className="hidden lg:block w-48 xl:w-56 shrink-0 object-contain drop-shadow-lg pointer-events-none"
            />
          </div>
        </div>
      </section>

      {/* Trailer */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <TrailerVideo
            src="/videos/trailer.mp4"
            poster="/videos/trailer-poster.jpg"
            title="What is Bedrock Toolchest? (60-second trailer)"
          />
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-slate-50 dark:bg-surface-card-dark">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">
            What&rsquo;s in the toolchest?
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-10 max-w-2xl">
            Version 3 is a ground-up rewrite &mdash; a single native executable, for Windows and Linux, that opens a
            multi-gigabyte world in about a second.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-10">
            See it in action
          </h2>
          <ScreenshotGallery screenshots={screenshots} avatar="/images/avatars/gallery_avatar.png" frame="/images/avatars/gallery_frame.png" />
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-slate-50 dark:bg-surface-card-dark">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-10">
            Up and running in minutes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Download', description: 'Grab the latest version. It\'s a single .exe file, no installation needed.', avatar: '/images/avatars/download_avatar.png' },
              { step: '2', title: 'Open a world', description: 'Open it up and pick any Minecraft Bedrock world from your computer.', avatar: '/images/avatars/open-world_avatar.png' },
              { step: '3', title: 'Edit and save', description: 'Make your changes, then hit save. Backups happen automatically.', avatar: '/images/avatars/save-world_avatar.png' },
              { step: '4', title: 'Open in Minecraft', description: 'Launch your world in Minecraft and see all your changes in action.', avatar: '/images/avatars/minecraft_avatar.png' },
            ].map(({ step, title, description, avatar }) => (
              <div key={step} className="relative pt-10 sm:pt-12">
                {/* Avatar */}
                <img
                  src={avatar}
                  alt=""
                  className="absolute top-0 right-3 sm:right-5 w-32 h-32 sm:w-40 sm:h-40 object-contain drop-shadow-lg pointer-events-none z-10"
                />

                {/* Card */}
                <div className="relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark h-full">
                  <div className="p-6 pr-32 sm:pr-40">
                    <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-400/10 text-primary-500 dark:text-primary-400 flex items-center justify-center font-bold text-sm mb-3">
                      {step}
                    </div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{title}</h3>
                  </div>
                  <div className="px-6 pb-6">
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4">
            Convinced yet?
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-lg mx-auto">
            Completely free &mdash; no paywalls, no subscriptions, no catch. Ever. And there's a whole Discord full of people ready to help if you get stuck.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <DownloadButton platform="windows" primary />
            <DownloadButton platform="linux" primary />
            <a
              href={siteConfig.discordUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-medium transition-colors"
            >
              Join Discord
            </a>
          </div>
        </div>
        {/* Discord avatar */}
        <img
          src="/images/avatars/discord_avatar.png"
          alt=""
          className="hidden md:block absolute bottom-0 right-[5%] lg:right-[10%] w-40 lg:w-48 object-contain drop-shadow-lg pointer-events-none"
        />
      </section>
    </>
  );
}
